/**
 * SyncStatusCard — the configured-state landing card for the
 * SubContractorHub integration on Settings → Integrations.
 *
 * Story 1.2 (FR59, FR60, NFR23, NFR27):
 * - Renders organization name, masked API key, last-sync timestamp, status
 *   badge, and a state-appropriate action area.
 * - Action buttons for Configure / Disable / Delete render but are inert
 *   in this story — Stories 1.3, 1.4, 1.5 will wire them up. The only
 *   functional button in 1.2 is "Re-test connection" in the auth_failed
 *   state, which reuses the Story 1.1 SubContractorHubPanel via the
 *   `onReconnect` callback.
 * - "What's included?" affordance opens ScopeSummaryDialog (FR59).
 *
 * Story 1.2 deliberately ships client-side status derivation (see
 * deriveIntegrationStatus.ts). If the derivation needs richer inputs in
 * later epics, escalate to a backend `/sync-status` endpoint per the
 * architectural decision documented in the story spec.
 */

import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Info, Loader2, Plug } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { useMissingProjects } from '@/hooks/integrations/useMissingProjects';
import { ScopeSummaryDialog } from './ScopeSummaryDialog';
import {
  deriveIntegrationStatus,
  type IntegrationStatus,
} from './deriveIntegrationStatus';
import type { SubContractorHubIntegration } from '@/types/integrations/subcontractorhub';

interface SyncStatusCardProps {
  integration: SubContractorHubIntegration;
  /**
   * Invoked when the user clicks "Re-test connection" in the auth_failed
   * state. Parent (IntegrationsTab) wires this to opening the existing
   * SubContractorHubPanel so the admin can paste a key and validate it.
   */
  onReconnect: () => void;
  /**
   * Invoked when the user clicks "Configure" in the active or paused
   * states. Parent wires this to opening the RotateKeyPanel (Story 1.3).
   */
  onConfigure: () => void;
  /**
   * Invoked when the user clicks "Disable" in the active state. Parent
   * opens an AlertDialog confirm, then fires the disable mutation
   * (Story 1.4).
   */
  onDisable: () => void;
  /**
   * Invoked when the user clicks "Enable" in the disabled state. Parent
   * fires the enable mutation directly — no confirm (Story 1.4).
   */
  onEnable: () => void;
  /**
   * True while the parent's enable mutation is in flight. Used to show
   * a spinner on the Enable button so cold-start latency isn't perceived
   * as a frozen UI.
   */
  enablePending?: boolean;
  /**
   * Invoked when the user clicks "Delete". Parent opens the
   * DeleteIntegrationDialog (typed-confirm) — Story 1.5.
   */
  onDelete: () => void;
  /**
   * Invoked when the user clicks "Reconnect". Parent opens the
   * ReconnectPanel (two-step typed-confirm) — Story 1.6.
   */
  onReconnectDifferentAccount: () => void;
  /**
   * SCH-4.2: Invoked when the user clicks "Sync Now" in the active state.
   * Parent fires the useSyncNow() mutation directly — no confirm; the
   * backend handles in-flight overlap by returning the existing run id.
   * Only shown when status === 'active'.
   */
  onSyncNow?: () => void;
  /**
   * True while the parent's syncNow mutation is in flight. Shows a
   * spinner on the Sync Now button.
   */
  syncNowPending?: boolean;
  /**
   * SCH-5.5: invoked when the user clicks Resume on the paused_drop_detected
   * state's typed-confirm dialog. Parent fires the useResumeAfterDrop()
   * mutation directly.
   */
  onResumeAfterDrop?: () => void;
  /** True while the resume mutation is in flight. */
  resumeAfterDropPending?: boolean;
  /**
   * SCH-5.7: invoked when the user clicks Resume on the paused_failures state.
   * Parent fires the useResumeAfterFailures() mutation directly.
   */
  onResumeAfterFailures?: () => void;
  /** True while the resume-after-failures mutation is in flight. */
  resumeAfterFailuresPending?: boolean;
}

interface BadgeConfig {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
}

const BADGE_BY_STATUS: Record<IntegrationStatus, BadgeConfig> = {
  active: { label: 'Active', variant: 'default' },
  disabled: { label: 'Disabled', variant: 'secondary' },
  auth_failed: { label: 'Authentication failed', variant: 'destructive' },
  paused: { label: 'Paused', variant: 'secondary' },
  // SCH-5.5: project-count drop detected — actionable paused state with a
  // dedicated Resume flow (typed-confirm).
  paused_drop_detected: { label: 'Paused — count drop', variant: 'destructive' },
  // SCH-5.7: auto-paused after consecutive failed scheduled syncs.
  paused_failures: { label: 'Paused — repeated failures', variant: 'destructive' },
  // SyncStatusCard is only rendered when integration is non-null, so
  // not_configured shouldn't reach this map. Provided for type completeness.
  not_configured: { label: 'Not configured', variant: 'outline' },
};

export function SyncStatusCard({
  integration,
  onReconnect,
  onConfigure,
  onDisable,
  onEnable,
  onDelete,
  onReconnectDifferentAccount,
  onSyncNow,
  enablePending = false,
  syncNowPending = false,
  onResumeAfterDrop,
  resumeAfterDropPending = false,
  onResumeAfterFailures,
  resumeAfterFailuresPending = false,
}: SyncStatusCardProps) {
  const [scopeOpen, setScopeOpen] = useState(false);
  const status = deriveIntegrationStatus(integration);
  const badge = BADGE_BY_STATUS[status];

  // SCH-5.6: surface the missing-projects count as a small caption below the
  // status row. Query is cheap (30s staleTime + tenant-scoped index). Hides
  // when count is 0 OR the user lacks permission (403 → query.error path,
  // we just don't render rather than show an error).
  const missingProjectsQuery = useMissingProjects();
  const missingCount = missingProjectsQuery.data?.data?.totalCount ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-3">
          <Plug className="h-5 w-5 text-primary" />
          <CardTitle>SubContractorHub</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={badge.variant}>{badge.label}</Badge>
          {/* SCH-5.3: secondary drift badge. Set when /discover-catalog's
              most recent run computed a fingerprint that differed from the
              previously-stored one. Cleared on next discover where fingerprints
              match. Clicking opens the Configure panel routed to Field
              Mapping where the full DriftBanner lives. */}
          {integration.lastSchemaDriftAt && (
            <Badge
              variant="outline"
              className="border-amber-300 text-amber-700 cursor-pointer hover:bg-amber-50"
              onClick={onConfigure}
              title="SubContractorHub schema has changed — open Field Mapping to review"
            >
              Schema drift
            </Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setScopeOpen(true)}
            className="text-xs"
          >
            <Info className="mr-1 h-3.5 w-3.5" />
            What's included?
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm">
          <span className="text-muted-foreground">Organization: </span>
          <span className="font-medium">{integration.externalOrgName ?? '—'}</span>
        </div>
        {integration.apiKeyLastFour && (
          <div className="text-sm">
            <span className="text-muted-foreground">API key: </span>
            <span className="font-mono">••••{integration.apiKeyLastFour}</span>
          </div>
        )}
        <div className="text-sm">
          <span className="text-muted-foreground">Last sync: </span>
          {(() => {
            // The sync engine never populates last_sync_at; the real "last synced"
            // signal is the most-recent scheduled tick (last_scheduled_sync_at).
            // Show the newest available timestamp — ISO 8601 strings sort
            // chronologically — so the card stops showing an em dash once syncing.
            const candidates = [integration.lastSyncAt, integration.lastScheduledSyncAt]
              .filter((t): t is string => Boolean(t))
              .sort();
            const lastSync = candidates.length ? candidates[candidates.length - 1] : null;
            return lastSync ? (
              <span title={lastSync} className="font-medium">
                {formatDistanceToNow(new Date(lastSync), { addSuffix: true })}
              </span>
            ) : (
              <span title="Sync has not run yet" className="font-medium">
                —
              </span>
            );
          })()}
        </div>
        {/* SCH-5.5: explanatory caption for the paused_drop_detected state.
            Surfaces context before the admin reaches for the Resume button. */}
        {status === 'paused_drop_detected' && (
          <div className="text-xs text-destructive">
            Sync paused because SubContractorHub returned far fewer projects
            than the previous successful sync. Investigate (was a SCH-side
            archive intentional?) before resuming.
          </div>
        )}
        {/* SCH-5.7: explanatory caption for the paused_failures state. */}
        {status === 'paused_failures' && (
          <div className="text-xs text-destructive">
            Scheduled sync paused after {integration.autoPauseThreshold}{' '}
            consecutive failures. Fix the underlying issue (often an expired or
            rotated API key) before resuming.
          </div>
        )}
        {/* SCH-5.6: missing-projects caption. Click routes to Configure
            panel; user navigates to the "Missing Projects" tab themselves
            (deep-linking to a specific tab is a small polish item; the
            count + presence is the load-bearing diagnostic). */}
        {missingCount > 0 && (
          <div className="text-xs">
            <span className="text-amber-700">
              <strong>{missingCount}</strong> project
              {missingCount === 1 ? '' : 's'} missing in SubContractorHub
            </span>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 ml-2 text-xs"
              onClick={onConfigure}
            >
              Review
            </Button>
          </div>
        )}
        <ActionArea
          status={status}
          onReconnect={onReconnect}
          onConfigure={onConfigure}
          onDisable={onDisable}
          onEnable={onEnable}
          onDelete={onDelete}
          onReconnectDifferentAccount={onReconnectDifferentAccount}
          onSyncNow={onSyncNow}
          enablePending={enablePending}
          syncNowPending={syncNowPending}
          onResumeAfterDrop={onResumeAfterDrop}
          resumeAfterDropPending={resumeAfterDropPending}
          onResumeAfterFailures={onResumeAfterFailures}
          resumeAfterFailuresPending={resumeAfterFailuresPending}
        />
        {/* TODO(Epic-5): Drift detection banner slots above this comment.
            Render <DriftBanner integration={integration} /> from the schema-drift
            epic when shipped. */}
      </CardContent>
      <ScopeSummaryDialog open={scopeOpen} onOpenChange={setScopeOpen} />
    </Card>
  );
}

interface ActionAreaProps {
  status: IntegrationStatus;
  onReconnect: () => void;
  onConfigure: () => void;
  onDisable: () => void;
  onEnable: () => void;
  onDelete: () => void;
  onReconnectDifferentAccount: () => void;
  onSyncNow?: () => void;
  enablePending: boolean;
  syncNowPending: boolean;
  onResumeAfterDrop?: () => void;
  resumeAfterDropPending: boolean;
  onResumeAfterFailures?: () => void;
  resumeAfterFailuresPending?: boolean;
}

function ActionArea({
  status,
  onReconnect,
  onConfigure,
  onDisable,
  onEnable,
  onDelete,
  onReconnectDifferentAccount,
  onSyncNow,
  enablePending,
  syncNowPending,
  onResumeAfterDrop,
  resumeAfterDropPending,
  onResumeAfterFailures,
  resumeAfterFailuresPending = false,
}: ActionAreaProps) {
  if (status === 'auth_failed') {
    // Story 1.5 added Delete; Story 1.6 added Reconnect — both useful
    // escape hatches for a broken integration.
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        <Button onClick={onReconnect}>Re-test connection</Button>
        <Button variant="outline" onClick={onReconnectDifferentAccount}>
          Reconnect
        </Button>
        <Button variant="outline" onClick={onDelete}>
          Delete
        </Button>
      </div>
    );
  }

  if (status === 'disabled') {
    // Reconnect-while-disabled is a confusing workflow (user enables
    // first if they want to swap accounts) — keep this state minimal.
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        <Button onClick={onEnable} disabled={enablePending}>
          {enablePending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Enable
        </Button>
        <Button variant="outline" onClick={onDelete}>
          Delete
        </Button>
      </div>
    );
  }

  // SCH-5.5: paused_drop_detected — Resume (typed-confirm via parent) +
  // Configure + Delete. NOT a generic 'paused' branch — admin needs the
  // dedicated Resume affordance, not the 'Coming in Epic 4' disabled button.
  if (status === 'paused_drop_detected' && onResumeAfterDrop) {
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        <Button onClick={onResumeAfterDrop} disabled={resumeAfterDropPending}>
          {resumeAfterDropPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Resume
        </Button>
        <Button variant="outline" onClick={onConfigure}>
          Configure
        </Button>
        <Button variant="outline" onClick={onDelete}>
          Delete
        </Button>
      </div>
    );
  }

  // SCH-5.7: paused_failures — Resume scheduled sync + Configure + Delete.
  if (status === 'paused_failures' && onResumeAfterFailures) {
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        <Button onClick={onResumeAfterFailures} disabled={resumeAfterFailuresPending}>
          {resumeAfterFailuresPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Resume scheduled sync
        </Button>
        <Button variant="outline" onClick={onConfigure}>
          Configure
        </Button>
        <Button variant="outline" onClick={onDelete}>
          Delete
        </Button>
      </div>
    );
  }

  if (status === 'paused') {
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        <Button variant="outline" onClick={onConfigure}>
          Configure
        </Button>
        <Button disabled title="Coming in Epic 4">
          Resume
        </Button>
        <Button variant="outline" onClick={onReconnectDifferentAccount}>
          Reconnect
        </Button>
        <Button variant="outline" onClick={onDelete}>
          Delete
        </Button>
      </div>
    );
  }

  // active (default) — all four buttons wired across Stories 1.3–1.6,
  // plus SCH-4.2 Sync Now (only shown when the parent supplies onSyncNow).
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {onSyncNow && (
        <Button onClick={onSyncNow} disabled={syncNowPending}>
          {syncNowPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sync Now
        </Button>
      )}
      <Button variant="outline" onClick={onConfigure}>
        Configure
      </Button>
      <Button variant="outline" onClick={onDisable}>
        Disable
      </Button>
      <Button variant="outline" onClick={onReconnectDifferentAccount}>
        Reconnect
      </Button>
      <Button variant="outline" onClick={onDelete}>
        Delete
      </Button>
    </div>
  );
}
